/**
 * Abstract description of sidebar elements like a card of the cart-flow
 */
import { preventScroll, releaseScroll } from "../../Utils/PreventScroll";

export abstract class AbstractSidebar {
    static SELECTORS = {
        ITEM: "div.sidebar-item",
        HEADER: "header",
        TABS: "ul",
        BTN_CONTINUE: ":scope .button-box button:first-of-type",
        BTN_ABORT: ":scope .button-box button:last-of-type"
    };

    public static RENDER_DONE_EVENT = "flow-content-rendered";
    public static DESTROY_ALL_EVENT = "sidebar-destroy-all";

    private readonly templateId: string;
    private readonly headerTemplateId: string;
    private readonly tabsTemplateId: string;
    private readonly eventContinue: string;
    private disconnectController: AbortController;
    private readonly disconnectedSignal: AbortSignal;
    protected headerTemplate: HTMLTemplateElement;
    private tabsTemplate: HTMLTemplateElement;
    protected contentTemplate: HTMLTemplateElement;
    public content: HTMLDivElement;
    protected header: HTMLElement;
    protected tabs: HTMLUListElement;
    private readonly formWrapper: HTMLFormElement;

    /**
     * Init new abstract sidebar element (f.ex. for the cart flow)
     *
     * @param headerTemplateId element id for the sidebar header like template#cart-flow-header
     * @param tabsTemplateId element id for the sidebar's tab bar
     * @param eventContinue identifier for the continue event
     * @param templateId id of the template that should be rendered in the content section
     * @param formWrapper a reference to a form element that wraps the whole cart flow in case its steps represent steps of a whole form
     */
    protected constructor(
        headerTemplateId: string,
        tabsTemplateId: string,
        templateId: string,
        eventContinue: string,
        formWrapper: HTMLFormElement = null
    ) {
        this.disconnectController = new AbortController();
        this.disconnectedSignal = this.disconnectController.signal;
        this.headerTemplateId = headerTemplateId;
        this.tabsTemplateId = tabsTemplateId;
        this.eventContinue = eventContinue;
        this.templateId = templateId;
        this.formWrapper = formWrapper;
    }

    /**
     * Add content to body
     */
    protected addContent(): this {
        if (this.tabs) {
            this.content.prepend(this.tabs);
        }
        this.content.prepend(this.header);
        // In case a formWrapper was defined in the constructor,
        // the content should be added inside this form instead of the body
        if (this.formWrapper) {
            this.formWrapper.appendChild(this.content);
        } else {
            document.body.appendChild(this.content);
        }
        return this;
    }

    /**
     * Init templates required for the sidebar flow
     */
    protected initTemplates(): this {
        this.contentTemplate = document.querySelector<HTMLTemplateElement>(`#${this.templateId}`);
        this.headerTemplate = document.querySelector<HTMLTemplateElement>(this.headerTemplateId);
        this.tabsTemplate = document.querySelector<HTMLTemplateElement>(this.tabsTemplateId);
        return this;
    }

    /**
     * obtain header from template
     */
    protected initHeader(): this {
        this.header = this.headerTemplate.content
            .querySelector<HTMLElement>(AbstractSidebar.SELECTORS.HEADER)
            .cloneNode(true) as HTMLElement;
        return this;
    }

    /**
     * obtain tabs from template
     */
    protected initTabs(): this {
        this.tabs = this.tabsTemplate.content
            .querySelector<HTMLUListElement>(AbstractSidebar.SELECTORS.TABS)
            .cloneNode(true) as HTMLUListElement;
        return this;
    }

    /**
     * obtain content from template
     */
    protected initContent(): this {
        this.content = this.contentTemplate.content
            .querySelector<HTMLDivElement>(AbstractSidebar.SELECTORS.ITEM)
            .cloneNode(true) as HTMLDivElement;
        return this;
    }

    /**
     * determine if section is allowed to render
     */
    protected isAllowed(): boolean {
        this.initTemplates();
        return true;
    }

    /**
     * render contents
     */
    public render() {
        if (!this.isAllowed()) return;
        this.initHeader().initTabs().initContent().addContent().addListeners();
        preventScroll();
        window.dispatchEvent(new CustomEvent(AbstractSidebar.RENDER_DONE_EVENT));
        requestAnimationFrame(() => this.animate(true));
    }

    /**
     * Add event listeners
     */
    protected addListeners(): this {
        this.addContinueListener().addAbortListener().addCloseButtonListener().addDestroyAllListener();
        return this;
    }

    /**
     * Add continue listener
     */
    protected addContinueListener(): this {
        this.content.querySelector(AbstractSidebar.SELECTORS.BTN_CONTINUE).addEventListener(
            "click",
            () => {
                // Sidebar flows that contain simple choices instead of a complex form (f.ex. cartflow)
                const isValidForChoices = !Object.hasOwn(this, "choice") || this.content.dataset.choice;
                // Validity check in case the sidebar is wrapped by a form element (this.formWrapper)
                const isValidForWrappedForms = this.formWrapper == null || this.formWrapper.checkValidity();
                if (isValidForChoices && isValidForWrappedForms) {
                    window.dispatchEvent(new CustomEvent(this.eventContinue));
                }
            },
            { signal: this.disconnectedSignal }
        );
        return this;
    }

    /**
     * Add abort listener
     */
    protected addAbortListener(): this {
        this.content
            .querySelector(AbstractSidebar.SELECTORS.BTN_ABORT)
            .addEventListener("click", () => this.destroy(), { once: true, signal: this.disconnectedSignal });
        return this;
    }

    /**
     * Animate in content
     * @param {Boolean} active
     */
    public animate(active: boolean): this {
        requestAnimationFrame(() => this.content.classList.toggle("in", active));
        return this;
    }

    /**
     * Destruct
     */
    protected destroy() {
        this.disconnectController.abort();
        this.content.addEventListener(
            "transitionend",
            () => {
                // Remove child either from body or from a specified form wrapper
                if (this.formWrapper) {
                    this.formWrapper.removeChild(this.content);
                } else {
                    document.body.removeChild(this.content);
                }
                if (!document.querySelector(".sidebar-item")) {
                    releaseScroll();
                }
            },
            { once: true }
        );
        this.animate(false);
    }

    /**
     * Destruct this sidebar item in case an event to destroy all of them
     * was fired.
     * @private
     */
    private addDestroyAllListener(): this {
        // The event is also dispatched in this class, but there's no place where
        // references to other instances of this class and its inheritors are being stored.
        window.addEventListener(AbstractSidebar.DESTROY_ALL_EVENT, () => {
            this.destroy();
        });
        return this;
    }

    /**
     * Add event listener to close button that dispatches the destroy-all
     * @private
     */
    private addCloseButtonListener(): this {
        this.header.querySelector(".icon.close").addEventListener(
            "click",
            () => {
                window.dispatchEvent(new CustomEvent(AbstractSidebar.DESTROY_ALL_EVENT));
            },
            { once: true }
        );
        return this;
    }
}
